// ###### JWT UTILITIES
// Safe client-side JWT decoding and validation
// Note: Signature validation MUST be done on the backend
// This is only for reading token payload (exp, user_id, etc.)

// ##### DECODE JWT PAYLOAD
// Decodes JWT payload without signature validation (safe for reading data)
export function decodeJWT(token) {
  if (!token) return null;

  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error('Invalid JWT format');
    }

    const payload = parts[1];
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return decoded;
  } catch (error) {
    console.error('Failed to decode JWT:', error);
    return null;
  }
}

// ##### CHECK IF TOKEN IS EXPIRED
// Checks expiration locally (in seconds, converts to milliseconds)
export function isTokenExpired(token) {
  const payload = decodeJWT(token);
  if (!payload || !payload.exp) {
    return true; // If no exp claim, consider expired
  }

  // exp is in seconds, Date.now() is in milliseconds
  const expirationTime = payload.exp * 1000;
  const currentTime = Date.now();
  
  // Add 5 minute buffer to account for clock skew
  return expirationTime < (currentTime - 5 * 60 * 1000);
}

// ##### GET USER ID FROM TOKEN
// Extracts user_id from token payload
export function getUserIdFromToken(token) {
  const payload = decodeJWT(token);
  if (!payload) return null;

  return payload.user_id || payload.user || payload.id || payload.sub || null;
}

// ##### GET TOKEN EXPIRATION TIME
// Returns expiration timestamp in milliseconds
export function getTokenExpiration(token) {
  const payload = decodeJWT(token);
  if (!payload || !payload.exp) {
    return null;
  }

  return payload.exp * 1000; // Convert to milliseconds
}

// ##### GET TIME UNTIL EXPIRATION
// Returns milliseconds until token expires (negative if expired)
export function getTimeUntilExpiration(token) {
  const expiration = getTokenExpiration(token);
  if (!expiration) {
    return null;
  }

  return expiration - Date.now();
}

// ##### CHECK IF TOKEN IS VALID (LOCALLY)
// Checks if token exists and is not expired (local check only)
export function isTokenValidLocally(token) {
  if (!token) return false;
  return !isTokenExpired(token);
}
